// @givmo/passkeys — adding a passkey to the signed-in account, through the "Confirm it's you" gate
// (AS-WEB11). Shared by the setup offer (A1) and the Sign-in & security card (A4).
//
// The act is `@givmo/auth`'s `registerPasskey`, run through `@givmo/step-up`'s gate, so the factor
// rules' refusals open the sheet and the act runs once more after it: `step_up_required` (a first
// passkey on a sign-in that is not recent enough, or that did not answer the account's
// authenticator) opens the sheet with the person's own ways in; `passkey_confirmation_required` (an
// account that already holds a passkey) opens it passkey-only. The words are this package's rows
// (`passkey-step-up-copy.ts`). The gate's host is the nearest `StepUpHostProvider`.
//
// A refusal the person can act on is told apart: the server's passkey limit (`PASSKEY_LIMIT_REACHED`,
// `PASSKEYS_MAX_PER_USER`) sets `limitReached`, and a surface says so (D-AS-35 fix 7) instead of
// "Try again", which cannot help.
//
// So is a first passkey on a sign-in older than rule 1's window (AS-PWL-2e; Compliance's review-76,
// P1): its `step_up_required` (`action: add_passkey`) opens the sheet with the person's own ways in,
// and where they hold none here (an emailed-code or phone sign-in) the gate fails the act as
// `StepUpPasskeyFailure('none_registered')`. That ends `sign_in_needed`, carrying the refusal's own
// words ("…Sign in again, then add your passkey."), never "Try again": a retry meets the same
// wall, and only a new sign-in, a new emailed code included, opens the window again.
//
// ⚠ Safari asks for a fresh user gesture before it creates a credential. After a confirmation
// through the sheet, the retried ceremony can be refused for want of one; that reads as a dismissal
// (`cancelled`), and the person's next tap on the same button succeeds inside the window the
// confirmation opened.
//
// UNDER A SIGN-IN LOCK NOTHING STARTS (AS-WEB23). A surface offers no add while one binds the
// account, but a page drawn before the lock bound (a stale tab) still shows its button. So an add
// for a known account re-reads the security read first (`signInLockNow`) and ends `locked`,
// starting no ceremony, when it says so; the fresh read redraws the surface locked. A re-read that
// FAILS starts nothing either and ends `failed` (the surface's "That didn't work" line), never on the
// last answer it was asked to replace; the dark 404 goes on as before. The server would not always
// refuse it: it admits a first passkey on a fresh company sign-in to an account that has no other
// way in, and the lock then refuses that passkey at every sign-in.

import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { firstErrorCode, isGivmoError } from '@givmo/api-client';
import { AuthError, registerPasskey } from '@givmo/auth';
import {
  STEP_UP_REQUIRED_ERROR_CODE,
  StepUpPasskeyFailure,
  isStepUpCancelled,
  stepUpActionOf,
  useOptionalStepUpHost,
  useStepUpGate,
} from '@givmo/step-up';
import { MY_PASSKEYS_QUERY_KEY, invalidateAccountFactors } from './account-factors';
import {
  PasskeyOptionsUnusableError,
  createPasskeyRegistrationTransport,
} from './passkey-registration-transport';
import { passkeyRefusalCodes, passkeyStepUpCopy } from './passkey-step-up-copy';
import { signInLockNow } from './sign-in-lock';

/**
 * How one attempt to add a passkey ended. `locked`: a sign-in lock binds the account (AS-WEB23), so
 * nothing was attempted.
 */
export type AddPasskeyOutcome =
  | 'created'
  | 'cancelled'
  | 'unavailable'
  | 'sign_in_needed'
  | 'locked'
  | 'failed';

/** The server's refusal once the account holds as many passkeys as it stores. */
export const PASSKEY_LIMIT_REACHED_ERROR_CODE = 'PASSKEY_LIMIT_REACHED';

/** D-AS-35 fix 7 (Android's shipped line, word for word): retrying can't help, removing one can. */
export const PASSKEY_LIMIT_LINE =
  'You’ve added as many passkeys as we can store. Remove one to add another.';

/**
 * SC3, APPROVED (D-CCM-88, Jonathan 2026-10-14 20:19 PDT, "aprove recs"; Account Security's
 * D-AS-51): the button a stale first-passkey add offers (AS-PWL-2e).
 */
export const SIGN_IN_AGAIN_LABEL = 'Sign in again';

/**
 * Rule 1's own words for a first passkey on a sign-in older than its window — the backend's
 * APPROVED copy (D-AS-29, `FACTOR_CHANGE_RECENT_SIGN_IN_COPY`), shown when the refusal itself
 * carries none.
 */
export const RECENT_SIGN_IN_LINE =
  'For your security, a passkey can be added only within 10 minutes of signing in. Sign in again, then add your passkey.';

/** Rule 1's refusal of a FIRST passkey for want of a recent sign-in, as the gate passes it on. */
function staleFirstPasskeyRefusal(thrown: unknown): unknown {
  if (!(thrown instanceof StepUpPasskeyFailure) || thrown.reason !== 'none_registered') {
    return undefined;
  }
  const { refusal } = thrown;
  if (!isGivmoError(refusal) || firstErrorCode(refusal) !== STEP_UP_REQUIRED_ERROR_CODE) {
    return undefined;
  }
  return stepUpActionOf(refusal) === 'add_passkey' ? refusal : undefined;
}

/** The words a `sign_in_needed` add says: the refusal's own, else rule 1's approved line. */
export function signInNeededLineOf(thrown: unknown): string | null {
  const refusal = staleFirstPasskeyRefusal(thrown);
  if (refusal === undefined) return null;
  const detail = isGivmoError(refusal) ? refusal.entries[0]?.detail?.trim() : undefined;
  return detail !== undefined && detail.length > 0 ? detail : RECENT_SIGN_IN_LINE;
}

/**
 * Read a failed add: a dismissal (the sheet's or the browser's), a dark surface, a first passkey
 * only a new sign-in can add here, or a failure.
 */
export function addPasskeyOutcomeOf(
  thrown: unknown,
): Exclude<AddPasskeyOutcome, 'created' | 'locked'> {
  if (isStepUpCancelled(thrown)) return 'cancelled';
  if (thrown instanceof AuthError && thrown.kind === 'cancelled') return 'cancelled';
  if (thrown instanceof PasskeyOptionsUnusableError) return 'unavailable';
  if (isGivmoError(thrown) && thrown.status === 404) return 'unavailable';
  if (staleFirstPasskeyRefusal(thrown) !== undefined) return 'sign_in_needed';
  return 'failed';
}

export interface AddPasskey {
  /** Run one attempt, resolving how it ended (never rejects). */
  add: () => Promise<AddPasskeyOutcome>;
  pending: boolean;
  /**
   * The last attempt `failed` on the server's passkey limit (`PASSKEY_LIMIT_REACHED`): say
   * {@link PASSKEY_LIMIT_LINE}, not "Try again". Cleared by the next attempt.
   */
  limitReached: boolean;
  /**
   * The last attempt ended `sign_in_needed`: the words to say (the refusal's own), with a way to
   * sign in again where the host has one. `null` otherwise; cleared by the next attempt.
   */
  signInNeeded: string | null;
  /** The gate's sheet: render it beside the control. */
  dialog: ReactNode;
}

export function useAddPasskey(
  testIdPrefix = 'passkey-step-up',
  {
    uid = null,
  }: {
    /** The signed-in account, whose security read is asked about a lock before the add starts. */
    uid?: string | null;
  } = {},
): AddPasskey {
  const host = useOptionalStepUpHost();
  const holdsAuthenticator = host?.holdsAuthenticator;
  const prepareHost = host?.prepare;
  const copy = useMemo(() => passkeyStepUpCopy(), []);
  const refusalCodes = useMemo(() => passkeyRefusalCodes(holdsAuthenticator), [holdsAuthenticator]);
  const gate = useStepUpGate({ copy, testIdPrefix, refusalCodes });
  const queryClient = useQueryClient();
  const [pending, setPending] = useState(false);
  const [limitReached, setLimitReached] = useState(false);
  const [signInNeeded, setSignInNeeded] = useState<string | null>(null);
  const { run } = gate;

  const add = useCallback(async (): Promise<AddPasskeyOutcome> => {
    setPending(true);
    setLimitReached(false);
    setSignInNeeded(null);
    try {
      const lock = await signInLockNow(queryClient, uid);
      if (lock === 'locked') return 'locked';
      if (lock === 'no_answer') return 'failed';
      await run(() => registerPasskey(createPasskeyRegistrationTransport()));
      await queryClient.invalidateQueries({ queryKey: MY_PASSKEYS_QUERY_KEY });
      // A host that reads only on demand (AS-WEB15) is not refetched by the invalidation: read it now,
      // so a caller waiting on the new passkey (Compliance's Sign after setup) finds it on the host.
      if (prepareHost !== undefined) await prepareHost().catch(() => undefined);
      return 'created';
    } catch (thrown) {
      const outcome = addPasskeyOutcomeOf(thrown);
      if (
        outcome === 'failed' &&
        isGivmoError(thrown) &&
        firstErrorCode(thrown) === PASSKEY_LIMIT_REACHED_ERROR_CODE
      ) {
        setLimitReached(true);
      }
      if (outcome === 'sign_in_needed') setSignInNeeded(signInNeededLineOf(thrown));
      // A refusal can rest on what this page last read about the account (a passkey added or removed
      // elsewhere since): re-read, so the next attempt runs on the server's answer.
      if (outcome !== 'cancelled') void invalidateAccountFactors(queryClient);
      return outcome;
    } finally {
      setPending(false);
    }
  }, [run, queryClient, prepareHost, uid]);

  return { add, pending, limitReached, signInNeeded, dialog: gate.dialog };
}
